import React, { useEffect, useState } from 'react';
import { useGamification } from '../context/GamificationContext';
import { useLanguage } from '../context/LanguageContext';
import { X, Star, Trophy, Zap } from 'lucide-react';

// Badge Notification Popup
export function BadgeNotification() {
  const { showBadgeNotification, setShowBadgeNotification } = useGamification();
  const { language } = useLanguage();

  if (!showBadgeNotification) return null;

  const badge = showBadgeNotification;

  return (
    <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-[9999] animate-bounce-in">
      <div className="bg-gradient-to-r from-yellow-400 via-yellow-500 to-orange-500 rounded-2xl p-1 shadow-2xl">
        <div className="bg-white rounded-xl p-4 flex items-center gap-4">
          <div className="text-5xl animate-pulse">{badge.icon}</div>
          <div>
            <p className="text-xs text-yellow-600 font-semibold uppercase tracking-wide">
              {language === 'en' ? 'Badge Unlocked!' : 'Badge Débloqué !'}
            </p>
            <h3 className="text-lg font-bold text-gray-900">
              {badge.name[language] || badge.name.fr}
            </h3>
            <p className="text-sm text-gray-600">
              {badge.description[language] || badge.description.fr}
            </p>
            <p className="text-xs text-yellow-600 mt-1 font-medium">
              +{badge.points} points
            </p>
          </div>
          <button 
            onClick={() => setShowBadgeNotification(null)}
            className="text-gray-400 hover:text-gray-600 p-1"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}

// Points Animation (floating +X points)
export function PointsAnimation() {
  const { showPointsAnimation } = useGamification();

  if (!showPointsAnimation) return null;

  return (
    <div className="fixed top-32 right-8 z-[9999] pointer-events-none">
      <div className="animate-float-up text-2xl font-bold text-green-500 flex items-center gap-2">
        <Zap className="w-6 h-6 fill-yellow-400 text-yellow-400" />
        +{showPointsAnimation.amount}
      </div>
    </div>
  );
}

// Level Progress Bar with hover to show next levels
export function LevelProgressBar({ compact = false }) {
  const { points, getLevel, getLevelProgress, levels } = useGamification();
  const { language } = useLanguage();
  const [showAllLevels, setShowAllLevels] = useState(false);
  const level = getLevel();
  const { progress, pointsToNext, nextLevel } = getLevelProgress();

  if (compact) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-lg">{level.icon}</span>
        <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-yellow-400 to-yellow-600 transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
        <span className="text-sm font-medium text-gray-600">{points}</span>
      </div>
    );
  }

  return (
    <div 
      className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-4 border border-gray-200 relative cursor-pointer group"
      onMouseEnter={() => setShowAllLevels(true)}
      onMouseLeave={() => setShowAllLevels(false)}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-2xl">{level.icon}</span>
          <div>
            <p className="font-bold text-gray-900">{level.name[language] || level.name.fr}</p>
            <p className="text-xs text-gray-500">{points} points</p>
          </div>
        </div>
        {nextLevel && (
          <div className="text-right">
            <p className="text-xs text-gray-500">
              {language === 'en' ? 'Next:' : 'Suivant:'} {nextLevel.name[language] || nextLevel.name.fr}
            </p>
            <p className="text-xs text-gray-400">{pointsToNext} pts</p>
          </div>
        )}
      </div>
      <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
        <div 
          className="h-full bg-gradient-to-r from-yellow-400 via-yellow-500 to-orange-500 transition-all duration-700 ease-out"
          style={{ width: `${progress}%` }}
        />
      </div>
      
      {/* Hover tooltip showing all levels */}
      {showAllLevels && (
        <div className="absolute left-0 right-0 top-full mt-2 bg-white rounded-xl shadow-xl border border-gray-200 p-4 z-50 animate-in fade-in slide-in-from-top-2 duration-200">
          <p className="text-xs font-semibold text-gray-500 uppercase mb-3">
            {language === 'en' ? 'All Levels' : 'Tous les niveaux'}
          </p>
          <div className="space-y-2">
            {levels.map((lvl, idx) => {
              const isCurrentLevel = lvl.minPoints === level.minPoints;
              const isUnlocked = points >= lvl.minPoints;
              return (
                <div 
                  key={lvl.name.fr}
                  className={`flex items-center justify-between p-2 rounded-lg ${
                    isCurrentLevel ? 'bg-yellow-50 border border-yellow-200' : 
                    isUnlocked ? 'bg-gray-50' : 'opacity-60'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-xl">{lvl.icon}</span>
                    <span className={`font-medium ${isCurrentLevel ? 'text-yellow-700' : 'text-gray-700'}`}>
                      {lvl.name[language] || lvl.name.fr}
                    </span>
                  </div>
                  <div className="text-right">
                    <span className={`text-sm ${isUnlocked ? 'text-green-600' : 'text-gray-400'}`}>
                      {lvl.minPoints} pts
                    </span>
                    {isCurrentLevel && (
                      <span className="ml-2 text-xs bg-yellow-200 text-yellow-800 px-2 py-0.5 rounded-full">
                        {language === 'en' ? 'Current' : 'Actuel'}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// Badges Display with hover for next objectives
export function BadgesDisplay({ showLocked = true, showNextGoal = true }) {
  const { unlockedBadges, badges } = useGamification();
  const { language } = useLanguage();
  
  // Find the next badge to unlock (first one not unlocked)
  const nextBadge = Object.values(badges).find(b => !unlockedBadges.includes(b.id));
  const unlockedCount = unlockedBadges.length;
  const totalBadges = Object.values(badges).length;

  return (
    <div className="space-y-4">
      {/* Next goal indicator */}
      {showNextGoal && nextBadge && (
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-3 border border-purple-100">
          <p className="text-xs text-purple-600 font-semibold mb-2">
            {language === 'en' ? '🎯 Next goal:' : '🎯 Prochain objectif :'}
          </p>
          <div className="flex items-center gap-3">
            <span className="text-2xl">{nextBadge.icon}</span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-800 truncate">{nextBadge.name[language] || nextBadge.name.fr}</p>
              <p className="text-xs text-gray-500 line-clamp-1">{nextBadge.description[language] || nextBadge.description.fr}</p>
            </div>
            <span className="text-xs text-purple-600 font-bold bg-purple-100 px-2 py-1 rounded-full whitespace-nowrap">+{nextBadge.points} pts</span>
          </div>
        </div>
      )}
      
      {/* Progress indicator */}
      <div className="flex items-center justify-between px-1">
        <span className="text-xs text-gray-500">{language === 'en' ? 'Badges unlocked:' : 'Badges débloqués :'}</span>
        <span className="text-sm font-bold text-gray-700">{unlockedCount} / {totalBadges}</span>
      </div>
      
      {/* Badges grid - responsive 2 rows of 5 */}
      <div className="grid grid-cols-5 gap-2">
        {Object.values(badges).map(badge => {
          const isUnlocked = unlockedBadges.includes(badge.id);
          
          if (!showLocked && !isUnlocked) return null;

          // Get short name for display (max 8 chars)
          const displayName = badge.name[language] || badge.name.fr;
          const shortName = displayName.length > 10 ? displayName.substring(0, 8) + '...' : displayName;

          return (
            <div
              key={badge.id}
              className={`relative group flex flex-col items-center justify-center py-2 px-1 rounded-xl transition-all cursor-pointer ${
                isUnlocked 
                  ? 'bg-yellow-50 border-2 border-yellow-300 hover:border-yellow-400 hover:shadow-md hover:scale-105' 
                  : 'bg-gray-50 border border-gray-200 opacity-40 hover:opacity-70'
              }`}
            >
              <span className={`text-xl ${isUnlocked ? '' : 'grayscale'}`}>
                {badge.icon}
              </span>
              <span className="text-[9px] text-center text-gray-600 leading-tight mt-1 font-medium">
                {shortName}
              </span>
              
              {/* Tooltip */}
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2.5 bg-gray-900 text-white text-xs rounded-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 w-44 shadow-xl">
                <p className="font-bold mb-1.5 text-sm">{displayName}</p>
                <p className="text-gray-300 text-[11px] leading-relaxed">{badge.description[language] || badge.description.fr}</p>
                <p className="text-yellow-400 mt-2 font-bold text-sm">+{badge.points} pts</p>
                {!isUnlocked && (
                  <p className="text-gray-400 mt-1.5 border-t border-gray-700 pt-2 text-[10px]">
                    🔒 {language === 'en' ? 'Not yet unlocked' : 'Pas encore débloqué'}
                  </p>
                )}
                {isUnlocked && (
                  <p className="text-green-400 mt-1.5 border-t border-gray-700 pt-2 text-[10px]">
                    ✓ {language === 'en' ? 'Unlocked!' : 'Débloqué !'}
                  </p>
                )}
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Streak Display
export function StreakDisplay() {
  const { streak } = useGamification();
  const { language } = useLanguage();

  if (streak === 0) {
    return (
      <div className="flex items-center gap-2 text-gray-400 text-sm">
        <span className="text-base">🔥</span>
        <span className="font-medium">0 {language === 'en' ? 'streak' : 'série'}</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 px-3 py-1.5 bg-orange-100 text-orange-700 rounded-full text-sm font-semibold">
      <span className="text-base">🔥</span>
      <span>
        {streak} {language === 'en' ? 'streak' : 'série'}
      </span>
    </div>
  );
}

// Comparison Stats
export function ComparisonStats() {
  const { getComparisonStats } = useGamification();
  const { language } = useLanguage();
  const stats = getComparisonStats();

  return (
    <div className="flex-1 text-right">
      <p className="text-xs text-green-600 font-medium leading-snug">
        {stats.message[language] || stats.message.fr}
      </p>
    </div>
  );
}

// Animated Progress Circle
export function AnimatedProgressCircle({ percentage, size = 120, strokeWidth = 8 }) {
  const [animatedPercentage, setAnimatedPercentage] = useState(0);
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimatedPercentage(percentage);
    }, 100);
    return () => clearTimeout(timer);
  }, [percentage]);

  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDashoffset = circumference - (animatedPercentage / 100) * circumference;

  const getColor = () => {
    if (animatedPercentage >= 90) return '#22c55e';
    if (animatedPercentage >= 70) return '#84cc16';
    if (animatedPercentage >= 50) return '#eab308';
    if (animatedPercentage >= 25) return '#f97316';
    return '#ef4444';
  };

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#e5e7eb"
          strokeWidth={strokeWidth}
        />
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={getColor()}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          className="transition-all duration-1000 ease-out"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-bold text-gray-900">{Math.round(animatedPercentage)}%</span>
        <span className="text-xs text-gray-500">Complet</span>
      </div>
    </div>
  );
}

// Encouragement Toast
export function EncouragementToast({ message, onClose }) {
  useEffect(() => {
    const timer = setTimeout(onClose, 4000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className="fixed bottom-24 left-1/2 transform -translate-x-1/2 z-[9999] animate-slide-up">
      <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-6 py-3 rounded-full shadow-lg flex items-center gap-3">
        <Star className="w-5 h-5 fill-yellow-300 text-yellow-300" />
        <span className="font-medium">{message}</span>
      </div>
    </div>
  );
}

// Random Tip Display
export function RandomTip({ tip }) {
  if (!tip) return null;

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
      {tip}
    </div>
  );
}
